import styles from "@styles/Slider.module.css";
import { MathUtils } from "@utils/MathUtils";
import { useWindowEvent } from "@utils/useEvent";
import useResize from "@utils/useResize";
import { CSSProperties, PointerEvent, useCallback, useEffect, useRef, useState } from "react";

type SliderProps = {
    initial: number;
    disabled?: boolean;
    label?: string;
    size?: number;
    thumbsize?: number;
    thickness?: number;
    from?: number;
    to?: number;
    onDrag?: (value: number) => void;
    onRelease?: (value: number) => void;
}
export function Slider({ initial, disabled, label, size = 10, thumbsize = size, thickness = 2, from = 0, to = 1, onDrag, onRelease }: SliderProps) {
    const [percent, setPercent] = useState(MathUtils.mapRange(initial, from, to, 0, 1));
    const [min, setMin] = useState(0);
    const [max, setMax] = useState(1);
    const [dragging, setDragging] = useState(false);
    const [offset, setOffset] = useState(0);

    const slider = useRef<HTMLDivElement>(null);
    useResize(() => {
        if (!slider.current) return;
        const bounds = slider.current.getBoundingClientRect();
        setMin(bounds.left);
        setMax(bounds.right);
    }, true, [slider, setMin, setMax]);

    const _onDrag = useCallback((e: globalThis.PointerEvent) => {
        if (!dragging) return;
        const x = e.clientX + offset;
        setPercent(MathUtils.mapRange(x, min, max, 0, 1, true));
    }, [dragging, offset, min, max, setPercent]);
    useWindowEvent("pointermove", _onDrag);

    const _onDragEnd = useCallback((e: globalThis.PointerEvent) => {
        if (!dragging) return;
        setDragging(false);
    }, [dragging]);
    useWindowEvent("pointerup", _onDragEnd);

    const _onDragStart = useCallback((e: PointerEvent) => {
        setDragging(true);
        const center = MathUtils.mapRange(percent, 0, 1, min, max);
        setOffset(center - e.clientX);
    }, [setDragging, percent, min, max, setOffset]);

    useEffect(() => {
        const value = MathUtils.mapRange(percent, 0, 1, from, to);
        if (dragging) onDrag?.(value);
        else onRelease?.(value);
    }, [percent, dragging, from, to, onDrag, onRelease]);

    const rootStyle: CSSProperties = {};
    (rootStyle as any)["--size"] = `${size}rem`;
    (rootStyle as any)["--thumbsize"] = `${thumbsize}rem`;
    (rootStyle as any)["--thickness"] = `${thickness}rem`;

    return (
        <div ref={slider} className={styles.root} style={rootStyle} >
            <div className={styles.bar} />
            <div className={styles.fill} style={{ width: `${percent * 100}%` }} />
            <div className={styles.handle} style={{ left: `${percent * 100}%`, transform: `translate(${thumbsize - size}rem, 0)`, pointerEvents: disabled ? "none" : "initial" }} onPointerDown={_onDragStart}>
                <div className={styles.thumb}>
                    {label && <h3 className={styles.label}>{label}</h3>}
                </div>
            </div>
        </div>
    )
}